/**
 * Auto add / remove a free gift based on cart contents.
 * Dawn theme 15.3.3
 *
 * Logic:
 * - Any product added to cart (qty > 0, excluding the freebie itself) -> add freebie
 * - Cart has zero non-freebie items -> remove freebie
 *
 * Works by intercepting fetch() calls to /cart/add, /cart/change, /cart/update
 * (these are what Dawn's cart drawer + cart page use internally), then
 * re-checking the cart and syncing the freebie.
 */
(function () {
  const FREE_VARIANT_ID = 54323800637806;
  let syncing = false;

  // Ask the theme's own cart components which sections THEY expect — this
  // guarantees an exact match with what renderContents()/onCartUpdate() need,
  // instead of guessing section names ourselves (which was causing a broken
  // empty-cart render).
  function getCartSectionsToRender() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      return cartDrawer.getSectionsToRender().map((s) => s.section || s.id);
    }

    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
      return cartItems.getSectionsToRender().map((s) => s.section || s.id);
    }

    // Fallback if neither component exposes the method (older/custom theme edits)
    const sections = [];
    if (document.querySelector('cart-drawer')) {
      sections.push('cart-drawer', 'cart-icon-bubble', 'cart-live-region-text');
    }
    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems && mainCartItems.dataset.id) {
      sections.push(mainCartItems.dataset.id, 'cart-icon-bubble', 'cart-live-region-text');
      const footer = document.getElementById('main-cart-footer');
      if (footer && footer.dataset.id) sections.push(footer.dataset.id);
    }
    return [...new Set(sections)].filter(Boolean);
  }

  // Push the updated section HTML into Dawn's existing custom elements
  // instead of reloading the page.
  function refreshCartUI(response) {
    if (!response || !response.sections) return;

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.renderContents(response);
    }

    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.onCartUpdate === 'function') {
      cartItems.onCartUpdate();
    } else if (window.location.pathname.includes('/cart') && !cartDrawer) {
      // Fallback for setups where neither component is found
      window.location.reload();
    }
  }

  // Replace the freebie's price text with "Free", and hide its quantity
  // selector + remove button so customers can't manually change/remove it
  // (it's fully controlled by this script based on cart contents).
    function markFreebieAsFree() {
    const links = document.querySelectorAll(`[href*="variant=${FREE_VARIANT_ID}"]`);

    links.forEach((link) => {
      const row =
        link.closest('.cart-item') ||
        link.closest('tr.cart-item') ||
        link.closest('[id^="CartItem-"]') ||
        link.closest('[id^="CartDrawer-Item-"]');

      if (!row) return;

      // Existing code...
      row
        .querySelectorAll('.price, .cart-item__price, .price__regular, .price-item, .cart-item__totals .price')
        .forEach((el) => {
          el.textContent = 'Free';
        });

      row
        .querySelectorAll(
          'quantity-input, .quantity, .cart-item__quantity-wrapper, .cart-item__quantity, cart-remove-button'
        )
        .forEach((el) => {
          el.style.display = 'none';
        });
    });

    document.querySelectorAll('#CartDrawer .cart-item').forEach((row) => {
      const removeBtn = row.querySelector('[data-variant-id]');
      if (removeBtn && Number(removeBtn.dataset.variantId) === FREE_VARIANT_ID) {
        row.parentNode.prepend(row);
      }
    });
    }

  // Keep watching the cart drawer / cart page containers, since Dawn re-renders
  // their innerHTML on every cart change (which would otherwise reset the price back)
  function observeCartForFreebiePrice() {
    const targets = [document.querySelector('cart-drawer'), document.getElementById('main-cart-items')].filter(
      Boolean
    );
    if (!targets.length) return;

    const observer = new MutationObserver(() => {
      clearTimeout(observer._debounce);
      observer._debounce = setTimeout(markFreebieAsFree, 50);
    });

    targets.forEach((el) => observer.observe(el, { childList: true, subtree: true }));
  }

  async function getCart() {
    const res = await fetch('/cart.js', { cache: 'no-store' });
    return res.json();
  }

  async function addFreebie() {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: FREE_VARIANT_ID, quantity: 1 }],
        sections: getCartSectionsToRender(),
        sections_url: window.location.pathname,
      }),
    });
    const data = await res.json();
    if (!res.ok) {
      console.error('[Freebie] add failed:', res.status, data);
    }
    return data;
  }

  async function removeFreebie(lineKey) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
        sections: getCartSectionsToRender(),
        sections_url: window.location.pathname,
      }),
    });
    const data = await res.json();
    if (!res.ok) {
      console.error('[Freebie] remove failed:', res.status, data);
    }
    return data;
  }

  async function syncFreebie() {
    if (syncing) return;
    syncing = true;
    try {
      const cart = await getCart();
      const freebieLine = cart.items.find((i) => i.variant_id === FREE_VARIANT_ID);
      const otherQty = cart.items
        .filter((i) => i.variant_id !== FREE_VARIANT_ID)
        .reduce((sum, i) => sum + i.quantity, 0);

      if (otherQty > 0 && !freebieLine) {
        const res = await addFreebie();
        refreshCartUI(res);
      } else if (otherQty === 0 && freebieLine) {
        await removeFreebie(freebieLine.key);
        // This theme's empty-cart markup doesn't render correctly via AJAX
        // re-render, so do a one-time reload only for this specific transition
        // (cart going fully empty). Every other add/remove stays refresh-free.
        window.location.reload();
        return;
      }
      markFreebieAsFree();
    } catch (err) {
      console.error('[Freebie] sync failed:', err);
    } finally {
      syncing = false;
    }
  }

  // Intercept every cart AJAX call Dawn makes (add to cart, qty change, remove, etc.)
  // NOTE: some Dawn actions (e.g. the remove button) call /cart/change WITHOUT the
  // .js suffix, which returns HTML instead of JSON. We must NOT depend on parsing
  // the response body — just detect the URL and trigger a sync regardless of what
  // comes back, otherwise removal silently never fires.
  const originalFetch = window.fetch;
  window.fetch = function (...args) {
    const req = args[0];
    const url = typeof req === 'string' ? req : req?.url || '';
    const isCartCall = /\/cart\/(add|change|update)(\.js)?(\?|$)/.test(url);

    const fetchPromise = originalFetch.apply(this, args);

    if (isCartCall && !syncing) {
      fetchPromise
        .then(() => setTimeout(syncFreebie, 500)) // let Dawn's own call + drawer animation settle first
        .catch(() => setTimeout(syncFreebie, 500)); // still sync even if the call errored
    }

    return fetchPromise;
  };

  // Also sync on plain page load (direct /cart visit, back button, etc.)
  document.addEventListener('DOMContentLoaded', () => {
    syncFreebie();
    markFreebieAsFree();
    observeCartForFreebiePrice();
  });

  // Safety net: also trigger a sync whenever a remove button or quantity control
  // is used, in case a particular click doesn't go through fetch() at all.
  document.addEventListener('click', (event) => {
    if (event.target.closest('cart-remove-button, [href*="/cart/change"], [data-cart-remove], .cart-remove-button')) {
      setTimeout(syncFreebie, 600);
    }
  });
  document.addEventListener('change', (event) => {
    if (event.target.matches('input[name="updates[]"], input[name="quantity"], .quantity__input')) {
      setTimeout(syncFreebie, 600);
    }
  });
})();
